import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useApproveTimesheetMutation,
  useRejectTimesheetMutation,
} from '../../hooks/useTimesheetMutations';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../components/workflow';
import { Spinner } from '../../components/common/Spinner';
import { MonthGrid } from '../../components/time/MonthGrid';
import { TimeEntryList } from '../../components/time/TimeEntryList';
import {
  getTimesheetDetails,
  getAllTimesheets,
  adminApproveTimesheet,
  adminReopenTimesheet,
  type Project,
} from '../../services/api/timeEntry';
import { getAllTimeOffs } from '../../services/api/timeOff';
import { getAdminUser } from '../../services/api/admin';
import { formatMoney } from '../../utils/format';
import { formatMonthYear } from '../../utils/dateFormat';
import { useEmployeeTypes } from '../../hooks/useAdmin';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import { StateHistory } from '../../components/workflow/StateHistory';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

// MKS-118: BE Zod schema requires reason 10–500 chars (Phase 28 D2 — UX-BRIEF S3
// lock). Mirror the constraint on the inline reject input so the button stays
// disabled with a visible counter, instead of letting a < 10-char request
// silently 400 against the API.
const MIN_REJECT_REASON = 10;
const MAX_REJECT_REASON = 500;

function getUserName(user: { firstName: string | null; lastName: string | null; email: string } | null): string {
  if (!user) return 'Unknown';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

export function TimesheetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Selection state for bulk actions
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [rejectReason, setRejectReason] = useState('');
  const [toast, setToast] = useState<AppToastState | null>(null);

  // Status filter state (empty means show all)
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());

  const timesheetId = parseInt(id || '0', 10);

  const { data: auditTrail } = useAuditTrail('Timesheet', timesheetId);

  // Fetch current timesheet details
  const {
    data: timesheetData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['timesheet', timesheetId],
    queryFn: () => getTimesheetDetails(timesheetId),
    enabled: timesheetId > 0,
  });

  const timesheet = timesheetData?.data;
  const userId = timesheet?.user?.id;

  // Fetch all timesheets for this user (for period navigation)
  const { data: userTimesheetsData } = useQuery({
    queryKey: ['allTimesheets', { userId }],
    queryFn: () => getAllTimesheets(1, undefined, undefined, userId),
    enabled: !!userId,
  });

  // Parse year from period for time-off query
  const periodYear = timesheet?.period?.from
    ? new Date(timesheet.period.from).getFullYear()
    : new Date().getFullYear();

  // Fetch user details to check if freelancer
  const { data: userDetails } = useQuery({
    queryKey: ['adminUser', userId],
    queryFn: () => getAdminUser(userId!),
    enabled: !!userId,
  });

  // Fetch employee types to resolve freelancer status by name instead of hardcoded ID
  const { data: employeeTypes } = useEmployeeTypes();

  // Check if user is a freelancer (non-paid time off)
  const isFreelancer = useMemo(() => {
    if (!userDetails || !employeeTypes) return false;
    const empType = employeeTypes.find((t) => t.id === userDetails.employeeTypeId);
    return empType?.name?.toLowerCase() === 'freelancer';
  }, [userDetails, employeeTypes]);

  // Fetch approved time-offs for this user
  const { data: userTimeOffs } = useQuery({
    queryKey: ['allTimeOffs', { year: periodYear, state: 'approved' }],
    queryFn: () => getAllTimeOffs({ year: periodYear, state: 'approved' }),
    enabled: !!userId,
  });

  // Filter to only this user's time-offs and build the map
  const timeOffDays = useMemo(() => {
    const days = new Map<string, { hours: number; typeName: string }>();
    if (!userTimeOffs || !userId) return days;

    for (const timeOff of userTimeOffs) {
      if (timeOff.userId !== userId) continue;
      const typeName = timeOff.timeOffType?.name || 'Time Off';
      for (const day of timeOff.days) {
        if (day.date) {
          // Freelancers have non-paid time off, so show 0 hours
          const hours = isFreelancer ? 0 : day.hours;
          days.set(day.date, { hours, typeName });
        }
      }
    }
    return days;
  }, [userTimeOffs, userId, isFreelancer]);

  // Sort timesheets by period date for navigation
  const sortedUserTimesheets = useMemo(() => {
    const timesheets = userTimesheetsData?.data || [];
    return [...timesheets].sort((a, b) => {
      const dateA = a.period?.from || '';
      const dateB = b.period?.from || '';
      return dateB.localeCompare(dateA); // newest first
    });
  }, [userTimesheetsData]);

  // Find current position and adjacent timesheets
  const currentIndex = sortedUserTimesheets.findIndex((ts) => ts.id === timesheetId);
  const prevTimesheet = currentIndex > 0 ? sortedUserTimesheets[currentIndex - 1] : null;
  const nextTimesheet = currentIndex < sortedUserTimesheets.length - 1 ? sortedUserTimesheets[currentIndex + 1] : null;

  // Navigation handlers - use replace to avoid polluting browser history
  const handlePreviousMonth = useCallback(() => {
    if (prevTimesheet) {
      navigate(`/manager/timesheets/${prevTimesheet.id}`, { replace: true });
    }
  }, [prevTimesheet, navigate]);

  const handleNextMonth = useCallback(() => {
    if (nextTimesheet) {
      navigate(`/manager/timesheets/${nextTimesheet.id}`, { replace: true });
    }
  }, [nextTimesheet, navigate]);

  // Phase 28 D1 — centralised optimistic + invalidate hooks (Plan 28-05).
  // Fixes MKS-109 (approved row didn't disappear) + MKS-112 (no auto-refresh).
  const approveMutation = useApproveTimesheetMutation({
    onSuccessToast: () => {
      setSelectedEntryIds(new Set());
      setToast({ variant: 'success', message: 'Entries approved.' });
    },
    onErrorToast: (message) => {
      setToast({ variant: 'error', message: `Approval failed: ${message}` });
    },
  });

  const adminApproveMutation = useMutation({
    mutationFn: (entryIds?: number[]) => adminApproveTimesheet(timesheetId, entryIds),
    onSuccess: async () => {
      // Phase 28 D1 — admin path mirrors useApproveTimesheetMutation's
      // invalidation set so the admin queue + employee banner stay fresh.
      await queryClient.refetchQueries({ queryKey: ['timesheet', timesheetId] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['allTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['adminTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['adminTimesheetsAggregate'] });
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
      queryClient.invalidateQueries({ queryKey: ['cockpit'] });
      queryClient.invalidateQueries({ queryKey: ['currentTimesheet'] });
      queryClient.invalidateQueries({ queryKey: ['timesheetHistory'] });
      // MKS-118: also refresh entry rows so red ✗ + rejection reason clear
      // immediately after admin approve (parity with useApproveTimesheetMutation).
      queryClient.invalidateQueries({ queryKey: ['monthlyEntries'] });
      setSelectedEntryIds(new Set());
      setToast({ variant: 'success', message: 'Entries approved.' });
    },
    onError: (err: Error) => {
      setToast({
        variant: 'error',
        message: `Approval failed: ${err.message || 'unknown error'}`,
      });
    },
  });

  const rejectMutation = useRejectTimesheetMutation({
    onSuccessToast: () => {
      setSelectedEntryIds(new Set());
      setRejectReason('');
      setToast({ variant: 'success', message: 'Entries rejected.' });
    },
    onErrorToast: (message) => {
      setToast({ variant: 'error', message: `Rejection failed: ${message}` });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: () => adminReopenTimesheet(timesheetId),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['timesheet', timesheetId] });
      queryClient.invalidateQueries({ queryKey: ['pendingTimesheets'] });
      queryClient.invalidateQueries({ queryKey: ['allTimesheets'] });
    },
  });

  // Get manager context and filter entries
  const managerContext = timesheet?.managerContext;
  const managedProjectIds = managerContext?.managedProjectIds;
  const ownManagedProjectIds = managerContext?.ownManagedProjectIds;

  // Only show entries for managed projects (or all if admin)
  const managedEntries = useMemo(() => {
    if (!timesheet?.entries) return [];
    if (managedProjectIds === null) return timesheet.entries; // Admin sees all
    return timesheet.entries.filter((e) => managedProjectIds?.includes(e.projectId));
  }, [timesheet?.entries, managedProjectIds]);

  // Filter entries by status (if filters are selected)
  const filteredEntries = useMemo(() => {
    if (statusFilters.size === 0) return managedEntries; // Show all if no filter
    return managedEntries.filter((e) => statusFilters.has(e.state));
  }, [managedEntries, statusFilters]);

  // Toggle status filter
  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  // Selectable entries (only submitted ones)
  const selectableEntries = useMemo(() => {
    return managedEntries.filter((e) => e.state === 'submitted');
  }, [managedEntries]);

  // Extract unique projects from entries
  const projects: Project[] = useMemo(() => {
    const projectMap = new Map<number, Project>();
    for (const entry of managedEntries) {
      if (!projectMap.has(entry.projectId)) {
        projectMap.set(entry.projectId, {
          id: entry.projectId,
          name: entry.project.name,
          fullName: entry.project.fullName,
          state: 'in_progress',
          isDefault: false,
          mandatoryTasks: false,
          defaultRate: null,
          customer: null,
        });
      }
    }
    return Array.from(projectMap.values());
  }, [managedEntries]);

  // Parse year and month from period
  const { year, month } = useMemo(() => {
    if (!timesheet?.period?.from) {
      const now = new Date();
      return { year: now.getFullYear(), month: now.getMonth() };
    }
    const date = new Date(timesheet.period.from);
    return { year: date.getFullYear(), month: date.getMonth() };
  }, [timesheet?.period?.from]);

  // Format period name
  const periodName = useMemo(
    // MKS-351 — "October 2026" from the shared util.
    () => formatMonthYear(new Date(year, month)),
    [year, month]
  );

  // Toggle multiple entries (for cell click) - only toggles selectable (submitted) entries
  const toggleEntries = (entryIds: number[]) => {
    // Filter to only include selectable entry IDs
    const selectableIds = entryIds.filter((id) =>
      selectableEntries.some((e) => e.id === id)
    );
    if (selectableIds.length === 0) return;

    const newSelected = new Set(selectedEntryIds);
    // Check if all are currently selected
    const allSelected = selectableIds.every((id) => newSelected.has(id));

    if (allSelected) {
      // Deselect all
      selectableIds.forEach((id) => newSelected.delete(id));
    } else {
      // Select all
      selectableIds.forEach((id) => newSelected.add(id));
    }
    setSelectedEntryIds(newSelected);
  };

  // Toggle single entry (for checkbox in list)
  const toggleEntry = (entryId: number) => {
    const newSelected = new Set(selectedEntryIds);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      // Only add if it's selectable (submitted state)
      if (selectableEntries.some((e) => e.id === entryId)) {
        newSelected.add(entryId);
      }
    }
    setSelectedEntryIds(newSelected);
  };

  const toggleAll = () => {
    if (selectedEntryIds.size === selectableEntries.length) {
      setSelectedEntryIds(new Set());
    } else {
      setSelectedEntryIds(new Set(selectableEntries.map((e) => e.id)));
    }
  };

  // Toggle entries for projects the current user manages
  const myProjectEntries = useMemo(() => {
    if (!ownManagedProjectIds) return [];
    return selectableEntries.filter((e) => ownManagedProjectIds.includes(e.projectId));
  }, [selectableEntries, ownManagedProjectIds]);

  const toggleMyProjects = () => {
    const myProjectEntryIds = myProjectEntries.map((e) => e.id);
    const allMyProjectsSelected = myProjectEntryIds.every((id) => selectedEntryIds.has(id));

    if (allMyProjectsSelected && myProjectEntryIds.length > 0) {
      // Deselect all my project entries
      const newSelected = new Set(selectedEntryIds);
      myProjectEntryIds.forEach((id) => newSelected.delete(id));
      setSelectedEntryIds(newSelected);
    } else {
      // Select all my project entries
      const newSelected = new Set(selectedEntryIds);
      myProjectEntryIds.forEach((id) => newSelected.add(id));
      setSelectedEntryIds(newSelected);
    }
  };

  const isMyProjectsSelected = myProjectEntries.length > 0 && myProjectEntries.every((e) => selectedEntryIds.has(e.id));

  const isAllSelected = selectableEntries.length > 0 && selectedEntryIds.size === selectableEntries.length;

  // Calculate totals
  const totalHours = managedEntries.reduce((sum, e) => sum + e.hours, 0);
  const filteredHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);
  const selectedHours = managedEntries
    .filter((e) => selectedEntryIds.has(e.id))
    .reduce((sum, e) => sum + e.hours, 0);

  // Action handlers
  const handleApprove = () => {
    const entryIds = Array.from(selectedEntryIds);
    if (entryIds.length > 0) {
      // Admins use admin approval endpoint, managers use manager endpoint
      if (managedProjectIds === null) {
        adminApproveMutation.mutate(entryIds);
      } else {
        approveMutation.mutate({ id: timesheetId, entryIds });
      }
    }
  };

  // MKS-118: derived validity flags for the inline reject input. The button is
  // gated on `rejectReasonValid` and the helper text under the input shows what
  // the user needs to do to make the request acceptable to the BE.
  const rejectReasonTrimmedLength = rejectReason.trim().length;
  const rejectReasonTooShort = rejectReasonTrimmedLength < MIN_REJECT_REASON;
  const rejectReasonTooLong = rejectReasonTrimmedLength > MAX_REJECT_REASON;
  const rejectReasonValid = !rejectReasonTooShort && !rejectReasonTooLong;

  const handleReject = () => {
    if (!rejectReasonValid) return;
    const entryIds = Array.from(selectedEntryIds);
    if (entryIds.length > 0) {
      rejectMutation.mutate({ id: timesheetId, reason: rejectReason.trim(), entryIds });
    }
  };

  const handleApproveAll = () => {
    // Admins use full approval endpoint, managers use per-entry approval
    if (managedProjectIds === null) {
      adminApproveMutation.mutate(undefined);
    } else {
      const entryIds = selectableEntries.map((e) => e.id);
      if (entryIds.length > 0) {
        approveMutation.mutate({ id: timesheetId, entryIds });
      }
    }
  };

  const handleReopen = () => {
    reopenMutation.mutate();
  };

  const actionBar = (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {myProjectEntries.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isMyProjectsSelected}
                onChange={toggleMyProjects}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Select my projects ({myProjectEntries.length})
              </span>
            </label>
          )}
          {managedProjectIds === null && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Select all ({selectableEntries.length} entries)
              </span>
            </label>
          )}
          {selectedEntryIds.size > 0 && (
            <span className="text-sm text-gray-500">
              {selectedEntryIds.size} selected ({selectedHours.toFixed(1)}h)
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <input
              type="text"
              placeholder={`Rejection reason (min ${MIN_REJECT_REASON} chars)`}
              value={rejectReason}
              maxLength={MAX_REJECT_REASON + 50}
              onChange={(e) => setRejectReason(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-64"
            />
            {rejectReason.trim().length > 0 && rejectReasonTooShort && (
              <span className="mt-1 text-xs text-red-600">
                {MIN_REJECT_REASON - rejectReason.trim().length} more chars needed (min {MIN_REJECT_REASON})
              </span>
            )}
            {rejectReasonTooLong && (
              <span className="mt-1 text-xs text-red-600">
                {rejectReason.trim().length - MAX_REJECT_REASON} chars over the limit (max {MAX_REJECT_REASON})
              </span>
            )}
          </div>
          <button
            onClick={handleReject}
            // Phase 28 / review M-02: gate by ANY mutation pending so back-to-back
            // clicks can't corrupt the optimistic-rollback baseline.
            // MKS-118: also gate by 10–500 char range so we don't 400 silently.
            disabled={
              selectedEntryIds.size === 0 ||
              !rejectReasonValid ||
              rejectMutation.isPending ||
              approveMutation.isPending ||
              adminApproveMutation.isPending
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject Selected'}
          </button>
          <button
            onClick={handleApprove}
            disabled={
              selectedEntryIds.size === 0 ||
              rejectMutation.isPending ||
              approveMutation.isPending ||
              adminApproveMutation.isPending
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <CheckIcon className="h-4 w-4" />
            {(managedProjectIds === null ? adminApproveMutation.isPending : approveMutation.isPending) ? 'Approving...' : 'Approve Selected'}
          </button>
          {managedProjectIds === null && (
            <button
              onClick={handleApproveAll}
              disabled={
                rejectMutation.isPending ||
                approveMutation.isPending ||
                adminApproveMutation.isPending
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon className="h-4 w-4" />
              {adminApproveMutation.isPending ? 'Approving...' : 'Approve All'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !timesheet) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-medium text-gray-900">Timesheet not found</h2>
        <p className="mt-1 text-sm text-gray-500">
          The timesheet you&apos;re looking for doesn&apos;t exist or you don&apos;t have access.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 inline-block text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  // A submitted timesheet stays actionable even with zero selectable
  // (submitted-state) entries: entries pre-approved before submission leave
  // 0 h approvable, and without this the sheet is stuck in Pending Review
  // with no approve affordance anywhere (backend accepts any submitted
  // sheet). Approve/Reject Selected stay disabled via selectedEntryIds.
  const canTakeAction =
    timesheet.state === 'submitted' ||
    (timesheet.state === 'rejected' && selectableEntries.length > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          title="Go back"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {getUserName(timesheet.user ?? null)}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-gray-500">{timesheet.user?.email}</span>
            <StatusBadge status={timesheet.state} />
          </div>
        </div>
      </div>

      {/* Month Grid Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1">
            <button
              onClick={handleNextMonth}
              disabled={!nextTimesheet}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Older month"
              title={nextTimesheet ? `Go to ${nextTimesheet.period?.name}` : 'No older timesheet'}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handlePreviousMonth}
              disabled={!prevTimesheet}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Newer month"
              title={prevTimesheet ? `Go to ${prevTimesheet.period?.name}` : 'No newer timesheet'}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          <h2 className="text-lg font-semibold text-gray-900">{periodName}</h2>

          <div className="text-sm text-gray-500">
            {totalHours.toFixed(1)}h total
          </div>
        </div>

        {/* MonthGrid */}
        <MonthGrid
          year={year}
          month={month}
          entries={managedEntries}
          projects={projects}
          readOnly
          selectedEntryIds={selectedEntryIds}
          onToggleEntrySelection={toggleEntries}
          timeOffDays={timeOffDays}
        />
      </div>

      {/* Timesheet Costs Summary */}
      {managedEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Timesheet Costs Summary</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours × Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costs
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Investment
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Group entries by project + rate to show separate rows when rates differ
                  const projectTotals = managedEntries.reduce((acc, entry) => {
                    const hourlyRate = (entry.assignmentCosts && entry.assignmentCosts > 0)
                      ? entry.assignmentCosts
                      : (entry.userCosts || 0);
                    const key = `${entry.projectId}_${hourlyRate}`;
                    if (!acc[key]) {
                      acc[key] = {
                        projectName: entry.project.fullName || entry.project.name,
                        hours: 0,
                        costs: 0,
                        rate: hourlyRate,
                        investment: 0,
                      };
                    }
                    acc[key].hours += entry.hours;
                    acc[key].costs += entry.hours * hourlyRate;
                    acc[key].investment += entry.hours * (entry.investmentRate || 0);
                    return acc;
                  }, {} as Record<string, { projectName: string; hours: number; costs: number; rate: number; investment: number }>);

                  const projectRows = Object.values(projectTotals);
                  const totalHoursSum = projectRows.reduce((sum, p) => sum + p.hours, 0);
                  const totalCostsSum = projectRows.reduce((sum, p) => sum + p.costs, 0);
                  const totalInvestment = projectRows.reduce((sum, p) => sum + p.investment, 0);

                  return (
                    <>
                      {projectRows.map((project) => {
                        return (
                          <tr key={project.projectName} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {project.projectName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                              {/* D-03 (Phase 98-03): 'CZK' is the correct, permanent argument here —
                                  these figures come from entry.assignmentCosts / entry.userCosts, the
                                  internal cost ledger, which is CZK by construction. The Entry DTO
                                  carries no currency field (see services/api/timeEntry.ts), and per
                                  RESEARCH Pitfall 3 an org-level default_currency must never be used
                                  to relabel a figure that is already denominated. */}
                              {formatNumber(project.hours, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, LEGACY_CZ_NUMBER_LOCALE)}h × {formatMoney(project.rate, 'CZK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                              {formatMoney(project.costs, 'CZK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                              {project.investment > 0 ? formatMoney(project.investment, 'CZK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-blue-50 font-semibold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          TOTAL:
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatNumber(totalHoursSum, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, LEGACY_CZ_NUMBER_LOCALE)}h
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatMoney(totalCostsSum, 'CZK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {totalInvestment > 0 ? formatMoney(totalInvestment, 'CZK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Bar (Top) */}
      {canTakeAction && actionBar}

      {/* Entry List */}
      {managedEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Reported Entries</h2>
              <span className="text-sm text-gray-500">
                {filteredHours.toFixed(1)} of {totalHours.toFixed(1)} hours
                {statusFilters.size > 0 && ` (filtered)`}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-gray-500 mr-1">Filter by status:</span>
              {['open', 'submitted', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => toggleStatusFilter(status)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    statusFilters.has(status)
                      ? status === 'approved'
                        ? 'bg-green-100 text-green-700 ring-1 ring-green-500'
                        : status === 'submitted'
                          ? 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-500'
                          : status === 'rejected'
                            ? 'bg-red-100 text-red-700 ring-1 ring-red-500'
                            : 'bg-gray-200 text-gray-700 ring-1 ring-gray-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
              {statusFilters.size > 0 && (
                <button
                  onClick={() => setStatusFilters(new Set())}
                  className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <TimeEntryList
            entries={filteredEntries}
            selectableEntries={selectableEntries}
            selectedEntryIds={selectedEntryIds}
            onToggleEntry={toggleEntry}
            onToggleAll={toggleAll}
            // MKS-192: per-project checkbox in the group header. `toggleEntries`
            // already scopes to selectable entries and flips the whole set.
            onToggleProject={toggleEntries}
            readOnly={selectableEntries.length === 0}
          />
        </div>
      )}

      {/* Action Bar (Bottom) */}
      {canTakeAction && actionBar}

      {/* Info message when no entries to approve */}
      {(timesheet.state === 'submitted' || timesheet.state === 'rejected') && selectableEntries.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-700">
            No entries available for approval. They may have already been approved or belong to other project managers.
          </p>
        </div>
      )}

      {/* Info for non-actionable timesheets */}
      {timesheet.state !== 'submitted' && timesheet.state !== 'rejected' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              This timesheet is {timesheet.state}. {timesheet.state === 'open' ? 'It has not been submitted yet.' : ''}
            </p>
            {timesheet.state === 'approved' && managedProjectIds === null && (
              <button
                onClick={handleReopen}
                disabled={reopenMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {reopenMutation.isPending ? 'Reopening...' : 'Reopen'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>

      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
